import {
  type VirtualRowMeta,
  type VirtualizedListState,
} from '@/storybook/components/FormCollection/hooks/useVirtualizedOptions';
import React from 'react';

/** Dividers use their fixed `estimateSize`; headers are measured since collapsing shifts indices in the size cache. */
function virtualRowNeedsDynamicMeasure<T>(meta: VirtualRowMeta<T> | undefined): boolean {
  return !!meta && meta.kind !== 'divider';
}

export interface VirtualizedRowsProps<T> {
  /** State bundle from `useVirtualizedOptions` (`.virtualizedList`). */
  'virtualization': VirtualizedListState<T>;
  'renderRow': (meta: VirtualRowMeta<T>, index: number) => React.ReactNode;
  'className'?: string;
  'data-testid'?: string;
}

const VirtualizedRows = <T,>({
  virtualization,
  renderRow,
  className,
  'data-testid': dataTestId = 'virtualizedRows',
}: VirtualizedRowsProps<T>) => {
  const { shouldVirtualize, totalSize, virtualItems, virtualRowsData, measureElement } = virtualization;

  if (!shouldVirtualize) {
    return (
      <>
        {virtualRowsData.map((meta, idx) => (
          <React.Fragment key={`${meta.kind}-${idx}`}>{renderRow(meta, idx)}</React.Fragment>
        ))}
      </>
    );
  }

  const containerHeight = totalSize ?? 0;

  return (
    <div
      className={className}
      style={{ height: containerHeight, width: '100%', position: 'relative' }}
      data-testid={dataTestId}
    >
      {virtualItems.map(vItem => {
        const meta = virtualRowsData[vItem.index];
        const attachMeasure = virtualRowNeedsDynamicMeasure(meta);
        return (
          <div
            key={vItem.key}
            ref={attachMeasure ? measureElement : undefined}
            data-index={vItem.index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${vItem.start}px)`,
            }}
          >
            {renderRow(meta, vItem.index)}
          </div>
        );
      })}
    </div>
  );
};

export default VirtualizedRows;
